import type {
  Attempt,
  AttemptResult,
  LineProgress,
  PracticeSession,
  Script,
  ShowProgress,
  UserStats,
} from '@/types/domain';
import { EMPTY_LINE_PROGRESS } from '@/types/domain';
import { dateKey, daysBetweenKeys, startOfWeek } from '@/lib/date';

export interface ShowStatistics {
  totalLines: number;
  practicedLines: number;
  correctRecalls: number;
  revealedLines: number;
  totalAttempts: number;
  accuracy: number;
  /** Share of the user's lines with at least one correct recall. */
  memorized: number;
  progressPercent: number;
}

export interface SceneStatistics {
  sceneId: string;
  name: string;
  totalLines: number;
  practicedLines: number;
  memorized: number;
  accuracy: number;
  progressPercent: number;
  /** Every attempt on this scene's lines, split by how it went. */
  attempts: { correct: number; hesitated: number; revealed: number; missed: number; total: number };
  /** Full passes through the scene, taken as the most-attempted line in it. */
  timesPracticed: number;
}

export interface MissedLine {
  lineId: string;
  sceneId: string;
  sceneName: string;
  text: string;
  misses: number;
  attempts: number;
  accuracy: number;
}

export function emptyLineProgress(): LineProgress {
  return { ...EMPTY_LINE_PROGRESS, updatedAt: new Date().toISOString() };
}

/** Lines belonging to the user, in performance order. */
export function myLines(script: Script, roles: string[]) {
  if (roles.length === 0) return [];
  const mine = new Set(roles);
  return script.scenes.flatMap((scene) =>
    scene.lines
      .filter((line) => line.kind === 'dialogue' && mine.has(line.character))
      .map((line) => ({ line, scene })),
  );
}

export function applyAttempt(
  progress: ShowProgress,
  lineId: string,
  result: AttemptResult,
  now: Date = new Date(),
): ShowProgress {
  const previous = progress.lineStats[lineId] ?? emptyLineProgress();
  const timestamp = now.toISOString();

  const updated: LineProgress = {
    ...previous,
    attempts: previous.attempts + 1,
    correct: previous.correct + (result === 'correct' ? 1 : 0),
    hesitated: previous.hesitated + (result === 'hesitated' ? 1 : 0),
    revealed: previous.revealed + (result === 'revealed' ? 1 : 0),
    missed: previous.missed + (result === 'missed' ? 1 : 0),
    lastResult: result,
    lastPracticedAt: timestamp,
    updatedAt: timestamp,
  };

  return {
    ...progress,
    lineStats: { ...progress.lineStats, [lineId]: updated },
    updatedAt: timestamp,
  };
}

export function computeShowStatistics(
  script: Script,
  progress: ShowProgress,
  roles: string[],
): ShowStatistics {
  const lines = myLines(script, roles);
  const totalLines = lines.length;

  let practicedLines = 0;
  let correctRecalls = 0;
  let revealedLines = 0;
  let totalAttempts = 0;
  let memorized = 0;

  for (const { line } of lines) {
    const stat = progress.lineStats[line.lineId];
    if (!stat || stat.attempts === 0) continue;
    practicedLines += 1;
    correctRecalls += stat.correct;
    revealedLines += stat.revealed;
    totalAttempts += stat.attempts;
    if (isMemorized(stat)) memorized += 1;
  }

  return {
    totalLines,
    practicedLines,
    correctRecalls,
    revealedLines,
    totalAttempts,
    accuracy: ratio(correctRecalls, totalAttempts),
    memorized,
    progressPercent: Math.round(ratio(memorized, totalLines) * 100),
  };
}

/** A line counts as memorized once it has been recalled cleanly and was not last missed. */
export function isMemorized(stat: LineProgress): boolean {
  return stat.correct > 0 && stat.lastResult === 'correct';
}

export function computeSceneStatistics(
  script: Script,
  progress: ShowProgress,
  roles: string[],
): SceneStatistics[] {
  const mine = new Set(roles);
  return script.scenes
    .map((scene) => {
      const lines = scene.lines.filter((l) => l.kind === 'dialogue' && mine.has(l.character));
      let practiced = 0;
      let correct = 0;
      let attempts = 0;
      let memorized = 0;
      let hesitated = 0;
      let revealed = 0;
      let missed = 0;
      let timesPracticed = 0;

      for (const line of lines) {
        const stat = progress.lineStats[line.lineId];
        if (!stat || stat.attempts === 0) continue;
        practiced += 1;
        correct += stat.correct;
        attempts += stat.attempts;
        hesitated += stat.hesitated;
        revealed += stat.revealed;
        missed += stat.missed;
        timesPracticed = Math.max(timesPracticed, stat.attempts);
        if (isMemorized(stat)) memorized += 1;
      }

      return {
        sceneId: scene.sceneId,
        name: scene.name,
        totalLines: lines.length,
        practicedLines: practiced,
        memorized,
        accuracy: ratio(correct, attempts),
        progressPercent: Math.round(ratio(memorized, lines.length) * 100),
        attempts: { correct, hesitated, revealed, missed, total: attempts },
        timesPracticed,
      };
    })
    .filter((scene) => scene.totalLines > 0);
}

/** Practiced scenes ordered worst-first; unpracticed scenes are excluded as unknown. */
export function weakestScenes(scenes: SceneStatistics[], limit = 3): SceneStatistics[] {
  return scenes
    .filter((scene) => scene.practicedLines > 0)
    .sort((a, b) => a.accuracy - b.accuracy || b.totalLines - a.totalLines)
    .slice(0, limit);
}

export function mostMissedLines(
  script: Script,
  progress: ShowProgress,
  roles: string[],
  limit = 5,
): MissedLine[] {
  const result: MissedLine[] = [];

  for (const { line, scene } of myLines(script, roles)) {
    const stat = progress.lineStats[line.lineId];
    if (!stat || stat.attempts === 0) continue;
    const misses = stat.missed + stat.revealed + stat.hesitated;
    if (misses === 0) continue;
    result.push({
      lineId: line.lineId,
      sceneId: scene.sceneId,
      sceneName: scene.name,
      text: line.text,
      misses,
      attempts: stat.attempts,
      accuracy: ratio(stat.correct, stat.attempts),
    });
  }

  return result.sort((a, b) => b.misses - a.misses || a.accuracy - b.accuracy).slice(0, limit);
}

/** Line ids eligible for Missed Lines Practice. */
export function strugglingLineIds(progress: ShowProgress): Set<string> {
  const ids = new Set<string>();
  for (const [lineId, stat] of Object.entries(progress.lineStats)) {
    if (stat.lastResult && stat.lastResult !== 'correct') ids.add(lineId);
  }
  return ids;
}

// ---------------------------------------------------------------------------
// Streaks and practice time
// ---------------------------------------------------------------------------

export function applySession(stats: UserStats, session: PracticeSession): UserStats {
  const day = dateKey(new Date(session.endedAt));
  const dailyMs = { ...stats.dailyMs, [day]: (stats.dailyMs[day] ?? 0) + session.durationMs };
  const currentStreak = computeStreak(dailyMs, new Date(session.endedAt));

  return {
    totalPracticeMs: stats.totalPracticeMs + session.durationMs,
    currentStreak,
    longestStreak: Math.max(stats.longestStreak, currentStreak),
    lastPracticeDate: day,
    dailyMs,
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Consecutive days of practice ending today. A day practiced yesterday but not
 * today still counts, so the streak is not lost until a full day is skipped.
 */
export function computeStreak(dailyMs: Record<string, number>, today: Date = new Date()): number {
  const active = new Set(
    Object.entries(dailyMs)
      .filter(([, ms]) => ms > 0)
      .map(([day]) => day),
  );
  if (active.size === 0) return 0;

  const todayKey = dateKey(today);
  let cursor = todayKey;
  if (!active.has(cursor)) {
    const yesterday = dateKey(new Date(today.getTime() - 86_400_000));
    if (!active.has(yesterday)) return 0;
    cursor = yesterday;
  }

  let streak = 0;
  while (active.has(cursor)) {
    streak += 1;
    cursor = shiftKey(cursor, -1);
  }
  return streak;
}

function shiftKey(key: string, days: number): string {
  const [year, month, day] = key.split('-').map(Number);
  return dateKey(new Date(year ?? 1970, (month ?? 1) - 1, (day ?? 1) + days));
}

export function practiceMsThisWeek(dailyMs: Record<string, number>, today: Date = new Date()): number {
  const weekStart = dateKey(startOfWeek(today));
  return Object.entries(dailyMs)
    .filter(([day]) => daysBetweenKeys(weekStart, day) >= 0 && daysBetweenKeys(day, dateKey(today)) >= 0)
    .reduce((total, [, ms]) => total + ms, 0);
}

export function summarizeAttempts(attempts: Attempt[]): Record<AttemptResult, number> {
  const totals: Record<AttemptResult, number> = { correct: 0, hesitated: 0, revealed: 0, missed: 0 };
  for (const attempt of attempts) totals[attempt.result] += 1;
  return totals;
}

function ratio(numerator: number, denominator: number): number {
  return denominator === 0 ? 0 : numerator / denominator;
}
